import React, { useState } from 'react';
import { getUserWatchHistory, type WatchHistoryItem } from '../../api/packageService';
import { formatSeconds } from '../../utils/time';

interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  usertype: string;
  companyId?: string;
  companyName?: string;
  childCompanyName?: string;
}

interface SuperAdminUserTableProps {
  users: User[];
  showActions?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onSearch?: (searchTerm: string) => void;
  userTypeFilter?: string;
  onUserTypeFilterChange?: (userType: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  totalUsers: number;
  startIndex: number; // Add startIndex prop for serial numbering
  currentUserRole?: string; // Add currentUserRole prop
}

const SuperAdminUserTable: React.FC<SuperAdminUserTableProps> = ({ 
  users, 
  showActions = false, 
  onEdit, 
  onDelete,
  onSearch,
  userTypeFilter,
  onUserTypeFilterChange,
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  onItemsPerPageChange,
  totalUsers,
  startIndex,
  currentUserRole: propCurrentUserRole
}) => {
  // Get current user role from localStorage or prop
  const getCurrentUserRole = () => {
    // Use prop if provided, otherwise get from localStorage
    if (propCurrentUserRole) {
      return propCurrentUserRole.toUpperCase();
    }
    
    try {
      const storedUserData = localStorage.getItem('user');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        // Normalize the user type to uppercase for comparison
        const userType = userData.usertype || userData.userType || 'USER';
        return userType.toUpperCase();
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
    return 'USER';
  };

  const currentUserRole = getCurrentUserRole();

  const [historyModal, setHistoryModal] = useState<{ isOpen: boolean; userName: string; userId: string; data: WatchHistoryItem[]; loading: boolean; error: string | null }>({
    isOpen: false, userName: '', userId: '', data: [], loading: false, error: null,
  });

  const openWatchHistory = async (userId: string, userName: string) => {
    setHistoryModal({ isOpen: true, userName, userId, data: [], loading: true, error: null });
    const res = await getUserWatchHistory(userId);
    if (res.status === 200) {
      setHistoryModal(prev => ({ ...prev, loading: false, data: res.data || [] }));
    } else {
      setHistoryModal(prev => ({ ...prev, loading: false, error: res.message }));
    }
  };

  const closeWatchHistory = () => setHistoryModal({ isOpen: false, userName: '', userId: '', data: [], loading: false, error: null });

  const formatDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  // Function to get row CSS class based on user type
  const getRowClass = (userType: string) => {
    if (!userType) {
      return 'user-row';
    }
    
    switch (userType.toLowerCase()) {
      case 'super admin':
        return 'super-admin-row';
      case 'admin':
        return 'admin-row';
      case 'company owner':
        return 'company-owner-row';
      default:
        return 'user-row';
    }
  };

  // Function to determine if actions should be shown for a user
  const shouldShowActions = (userType: string, currentUserRole: string = 'SUPER_ADMIN') => {
    // For Admin role, restrict actions on other Admins and Super Admins
    if (currentUserRole.toUpperCase() === 'ADMIN') {
      // Admin can only edit/delete regular users
      return userType && userType.toLowerCase() === 'user';
    }
    
    // For Company Owner role, restrict actions on Admins, Super Admins and other Company Owners
    if (currentUserRole.toUpperCase() === 'COMPANY_OWNER') {
      // Company Owner can only edit/delete regular users and admins
      const lowerUserType = userType?.toLowerCase();
      return lowerUserType === 'user' || lowerUserType === 'admin';
    }
    
    // Super Admin can see actions for all users except other Super Admins
    return userType && userType.toLowerCase() !== 'super admin';
  };

  // Get unique user types for filter dropdown
  const getUserTypes = () => {
    const types = Array.from(new Set(users.map(user => user.usertype)));
    return ['All', ...types];
  };

  // Function to get background color for user type cell based on user type
  const getUserTypeBackgroundColor = (userType: string) => {
    if (!userType) {
      return '#FFFFFF'; // White for user
    }
    
    switch (userType.toLowerCase()) {
      case 'super admin':
        return '#ADD8E6'; // Light blue for super admin
      case 'admin':
        return '#FFFF99'; // Yellow for admin
      case 'company owner':
        return '#FFCC99'; // Light orange for company owner
      default:
        return '#FFFFFF'; // White for user
    }
  };

  return (
    <div className="table-container rounded-3 shadow-sm flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
      <style>
        {`
          /* Desktop: Scrollable table */
          @media (min-width: 768px) {
            .table-wrapper-mobile {
              overflow-x: auto !important;
              overflow-y: auto;
            }
            .table-wrapper-mobile table {
              width: 100%;
              min-width: 1200px;
            }
            .table-wrapper-mobile table th,
            .table-wrapper-mobile table td {
              text-align: left !important;
              padding: 0.75rem 1rem !important;
              font-size: 0.95rem;
            }
          }
          
          /* Mobile: Keep scrollable */
          @media (max-width: 767.98px) {
            .table-container {
              height: 100% !important;
              display: flex !important;
              flex-direction: column !important;
            }
            .table-filters-mobile {
              flex-shrink: 0;
              padding: 0.5rem !important;
            }
            .table-filters-mobile .form-control,
            .table-filters-mobile .form-select {
              font-size: 0.875rem;
              padding: 0.5rem;
            }
            .table-wrapper-mobile {
              flex: 1;
              min-height: 0;
              overflow-x: auto;
              overflow-y: auto;
              -webkit-overflow-scrolling: touch;
            }
            .table-wrapper-mobile table {
              min-width: 800px;
            }
            .table-wrapper-mobile table th,
            .table-wrapper-mobile table td {
              text-align: left !important;
              padding: 0.5rem 0.75rem !important;
              font-size: 0.875rem;
            }
            .table-wrapper-mobile thead {
              position: sticky;
              top: 0;
              z-index: 10;
            }
            .table-wrapper-mobile thead th {
              background-color: #0d6efd !important;
              color: white !important;
              white-space: nowrap;
            }
            .table-pagination-mobile {
              flex-shrink: 0;
              padding: 0.5rem !important;
              flex-wrap: wrap;
              gap: 0.5rem;
            }
            .table-pagination-mobile .pagination {
              flex-wrap: wrap;
              margin: 0;
            }
            .table-pagination-mobile .pagination .page-link {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
            }
          }
          
          /* Row styling for different user types */
          .super-admin-row {
            background-color: #e3f2fd !important;
          }
          
          .admin-row {
            background-color: #fffde7 !important;
          }
          
          .company-owner-row {
            background-color: #fff3e0 !important;
          }
          
          .user-row {
            background-color: #ffffff !important;
          }
        `}
      </style>
      {/* Search and Filter Controls - Fixed at top */}
      <div className="p-2 p-md-2 bg-light table-filters-mobile" style={{ flexShrink: 0 }}>
        <div className="row g-2">
          <div className="col-12 col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, email, phone..."
              onChange={(e) => onSearch && onSearch(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-6">
            <select
              className="form-select"
              value={userTypeFilter || 'All'}
              onChange={(e) => onUserTypeFilterChange && onUserTypeFilterChange(e.target.value)}
            >
              {getUserTypes().map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table Container - Scrollable and Responsive */}
      <div className="table-wrapper-mobile flex-grow-1" style={{ overflow: 'auto', minHeight: 0 }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-primary text-dark sticky-top" style={{ zIndex: 10 }}>
            <tr>
              <th className="py-2 px-2 px-md-2 text-start">#</th>
              <th className="py-2 px-2 px-md-2 text-start">Name</th>
              <th className="py-2 px-2 px-md-2 text-start">Email</th>
              <th className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">Company</th>
              <th className="py-2 px-2 px-md-2 d-none d-lg-table-cell text-start">Child Company</th>
              <th className="py-2 px-2 px-md-2 text-start">Type</th>
              <th className="py-2 px-2 px-md-2 d-none d-sm-table-cell text-start">Contact</th>
              {showActions && <th className="text-center py-2 px-2 px-md-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => {
              return (
                <tr key={user._id} className={getRowClass(user.usertype)}>
                  <td className="py-2 px-2 px-md-2 text-start">
                    <span className="fw-medium">{startIndex + index + 1}</span>
                  </td>
                  <td className="py-2 px-2 px-md-2 text-start">
                    <span className="fw-medium">{user.name}</span>
                  </td>
                  <td className="py-2 px-2 px-md-2 text-start">
                    <a href={`mailto:${user.email}`} className="text-decoration-none" style={{ color: '#1e3c72' }}>
                      {user.email}
                    </a>
                  </td>
                  <td className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">
                    {user.companyName || 'N/A'}
                  </td>
                  <td className="py-2 px-2 px-md-2 d-none d-lg-table-cell text-start">
                    {user.childCompanyName || 'None'}
                  </td>
                  <td className="py-2 px-2 px-md-2 text-start">
                    <span className="badge" style={{ 
                      backgroundColor: getUserTypeBackgroundColor(user.usertype), 
                      color: '#000000' 
                    }}>{user.usertype || 'N/A'}</span>
                  </td>
                  <td className="py-2 px-2 px-md-2 d-none d-sm-table-cell text-start">
                    {user.phone || 'N/A'}
                  </td>
                  {showActions && (
                    <td className="text-center py-2 px-2 px-md-2">
                      {shouldShowActions(user.usertype, currentUserRole) ? (
                        <div className="d-flex justify-content-center gap-1 gap-md-2">
                          <button
                            className="btn btn-outline-info btn-sm rounded-pill px-2 px-md-3"
                            onClick={() => openWatchHistory(user._id, user.name)}
                            title="Watch History"
                          >
                            <i className="bi bi-clock-history"></i>
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm rounded-pill px-2 px-md-3"
                            onClick={() => onEdit && onEdit(user._id)}
                            title="Edit User"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm rounded-pill px-2 px-md-3"
                            onClick={() => onDelete && onDelete(user._id)}
                            title="Delete User"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center gap-1 gap-md-2" style={{ opacity: 0.3 }}>
                          <button 
                            className="btn btn-outline-primary btn-sm rounded-pill px-2 px-md-3" 
                            disabled
                            title="Edit restricted"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-sm rounded-pill px-2 px-md-3" 
                            disabled
                            title="Delete restricted"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination - Fixed at bottom */}
      <div className="d-flex justify-content-between align-items-center p-2 p-md-2 table-pagination-mobile" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center flex-wrap">
          <span className="me-2 small">Items per page:</span>
          <select
            className="form-select form-select-sm"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            style={{ width: '80px' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="ms-2 ms-md-3 small">Total: {totalUsers} users</span>
        </div>
        
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                <span className="d-none d-sm-inline">Previous</span>
                <span className="d-sm-none">Prev</span>
              </button>
            </li>
            
            {/* Generate page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(pageNum)}>
                    {pageNum}
                  </button>
                </li>
              );
            })}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <li className="page-item disabled"><span className="page-link">...</span></li>
                <li className="page-item">
                  <button className="page-link" onClick={() => onPageChange(totalPages)}>
                    {totalPages}
                  </button>
                </li>
              </>
            )}
            
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                <span className="d-none d-sm-inline">Next</span>
                <span className="d-sm-none">Next</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
  

    {/* Watch History Modal */}
    {historyModal.isOpen && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '860px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
                <i className="bi bi-clock-history me-2" style={{ color: '#6366f1' }}></i>
                Watch History
              </h5>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{historyModal.userName}</p>
            </div>
            <button onClick={closeWatchHistory} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>✕</button>
          </div>

          {/* Body */}
          <div style={{ overflow: 'auto', flex: 1, padding: '16px 24px' }}>
            {historyModal.loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading watch history...
              </div>
            )}
            {historyModal.error && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
                <i className="bi bi-exclamation-circle me-2"></i>{historyModal.error}
              </div>
            )}
            {!historyModal.loading && !historyModal.error && historyModal.data.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <i className="bi bi-play-circle" style={{ fontSize: '40px', display: 'block', marginBottom: '12px', color: '#d1d5db' }}></i>
                This user hasn't watched any course or webinar yet.
              </div>
            )}
            {!historyModal.loading && historyModal.data.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Course / Webinar</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Type</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Progress</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Watch Time</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Started</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Last Watched</th>
                  </tr>
                </thead>
                <tbody>
                  {historyModal.data.map((item, idx) => (
                    <tr key={item.courseId} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', color: '#111827', fontWeight: 500, maxWidth: '220px' }}>
                        {item.courseTitle}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                          background: item.courseType === 'Webinar' ? '#ede9fe' : '#dbeafe',
                          color: item.courseType === 'Webinar' ? '#7c3aed' : '#1d4ed8',
                        }}>
                          {item.courseType === 'Webinar' ? '📡 Webinar' : '📘 Course'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                          <div style={{ width: '60px', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(item.progressPercent, 100)}%`, height: '100%', background: item.progressPercent >= 98 ? '#16a34a' : item.progressPercent >= 50 ? '#2563eb' : '#f59e0b', borderRadius: '3px' }}></div>
                          </div>
                          <span style={{ color: '#374151', fontWeight: 500 }}>{item.progressPercent}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#374151' }}>
                        {formatSeconds(item.totalWatchTime)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>{formatDate(item.startedAt)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>{formatDate(item.lastWatchedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          {!historyModal.loading && historyModal.data.length > 0 && (
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
              <span>{historyModal.data.length} course{historyModal.data.length !== 1 ? 's' : ''} watched</span>
              <span>Sorted by most recently watched</span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
  );
};

export default SuperAdminUserTable;